import i18next from 'i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import { signout } from '../actions';
import { store } from '../store';
import history from '../history';
import PATHS from '../Routers/PATHS';
import { savePath } from '../helpers/savePath';
import { toastOptions } from '../helpers/general';


const { dispatch, getState } = store;

export const BASE_URL = process.env.REACT_APP_BASE_URL;
// In dev, uploads must stay same-origin (/api, forwarded by the dev proxy in vite.config.mjs):
// the assets.<hostname> origin has no CORS headers and never receives the
// localhost session cookie, so the browser drops the response entirely.
export const BASE_URL_UPLOAD = BASE_URL;

const axiosInstance = axios.create({
    baseURL: BASE_URL,
    withCredentials: true
});

const reportedClientErrors = new WeakSet();

export const reportClientError = (error, context) => {
    const requestUrl = error?.config?.url || error?.response?.config?.url;
    if (requestUrl === '/public/log') return Promise.resolve();

    const isObjectError = error !== null && (typeof error === 'object' || typeof error === 'function');
    if (isObjectError && reportedClientErrors.has(error)) return Promise.resolve();
    if (isObjectError) reportedClientErrors.add(error);

    return axiosInstance.post('/public/log',
        {
            type: "CLIENT_ERROR",
            message: error?.message,
            userId: getState().auth?.user?.id,
            stack: error?.stack,
            moreInfo: { req: error?.request, res: error?.response, context },
            pageUrl: window.location.pathname
        },
        { headers: { "Log-Auth": (new Date().getUTCHours() * 19) ^ 19930727 } }
    ).catch(() => undefined);
};

axiosInstance.interceptors.request.use(
    (request) => {
        const isAdmin = getState().auth?.user?.isAdmin;
        if (isAdmin) {
            if (request.url) {
                request.url = request.url.replaceAll("%", "%25").replaceAll("+", "%2B");
            }

            if (request.method === 'post' || request.method === 'put' || request.method === 'delete') {
                const toastId = toast.loading(i18next.t("PleaseWait"), toastOptions());
                request['toastId'] = toastId;
            }
        }
        return request;
    },
    undefined
);

axiosInstance.interceptors.response.use(
    (response) => {
        const isAdmin = getState().auth?.user?.isAdmin;
        if (isAdmin) {
            if (response.config.method === 'post' || response.config.method === 'put' || response.config.method === 'delete') {
                toast.update(response.config.toastId, { render: i18next.t('Success'), type: "success", isLoading: false, autoClose: 2000, closeButton: true, ...toastOptions() });
            }
        }
        return response;
    },
    async (err) => {
        const user = getState().auth?.user;
        const isAdmin = user?.isAdmin;
        if (isAdmin) {
            if (err.response?.status === 401 && err.response?.config?.url !== '/auth/login' && !err.response?.config?.url?.includes('/fs/doc')) {
                return signout()(dispatch);
            }
            if (err.response?.data?.code) {
                toast.update(err.config.toastId, { render: i18next.t(err.response.data.code), type: "error", isLoading: false, autoClose: 5000, closeButton: true, ...toastOptions() });
                return new Error(i18next.t(err.response.data.code));
            } else toast.update(err.config.toastId, { render: i18next.t("ErrorBody"), type: "error", isLoading: false, autoClose: 5000, closeButton: true, ...toastOptions() });
        } else {
            if (err.response?.status === 401 && err.response?.config?.url !== '/auth/login' && !err.response?.config?.url?.includes('/fs/doc')) {
                savePath("beforeLogoutPathPref");
                await signout()(dispatch);
                return history.replace(PATHS.LANDING_SIGNIN.URL);
            }
            if ((err.config?.url || err.response?.config?.url) !== '/public/log') {
                reportClientError(err, err.config?.clientErrorContext);
            }
        }
        return Promise.reject(err);
    }
);

export default axiosInstance;
